import { z } from "zod";
import type { CharacterSheetDnd5e } from "@/lib/rules/dnd5e/character/character-sheet";

const SourcedRollModifierSchema = z.object({
  rollTag: z.string().optional(),
  modifier: z.number().optional(),
  count: z.number().optional(),
  sides: z.number().optional(),
  keepHighest: z.boolean().optional(),
  keepLowest: z.boolean().optional(),
  subtract: z.boolean().optional(),
  critThreshold: z.number().optional(),
  rerollOnceOn: z.number().optional(),
  source: z.record(z.unknown()),
});

const ModListSchema = z.array(SourcedRollModifierSchema).readonly();

const ActorSizeSchema = z.enum([
  "Tiny",
  "Small",
  "Medium",
  "Large",
  "Huge",
  "Gargantuan",
]);

const CharacterFeatureSourceSchema = z.enum([
  "class",
  "subclass",
  "species",
  "background",
  "feat",
  "equipment",
  "other",
]);

const CharacterFeatureDnd5eSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  source: CharacterFeatureSourceSchema,
  sourceLabel: z.string(),
  level: z.number().optional(),
});

const PendingProfileProficiencyChoiceDnd5eSchema = z.object({
  profileId: z.string(),
  label: z.string(),
  field: z.string(),
  count: z.number().int().positive(),
  pool: z.array(z.string()).readonly(),
});

const PendingExpertiseChoiceDnd5eSchema = z.object({
  classProfileId: z.enum(["profile_bard", "profile_rogue"]),
  label: z.string(),
  remaining: z.number().int().positive(),
  entitlement: z.number().int().positive(),
  selectedSkills: z.array(z.string()).readonly(),
  selectedTools: z.array(z.string()).readonly(),
  skillPool: z.array(z.string()).readonly(),
  toolPool: z.array(z.string()).readonly(),
});

const PendingMetamagicChoiceDnd5eSchema = z.object({
  classProfileId: z.literal("profile_sorcerer"),
  label: z.string(),
  remaining: z.number().int().positive(),
  selectedOptions: z.array(z.string()).readonly(),
  optionPool: z.array(z.string()).readonly(),
});

const PendingWeaponMasteryChoiceDnd5eSchema = z.object({
  classProfileId: z.enum(["profile_barbarian", "profile_rogue"]),
  label: z.string(),
  remaining: z.number().int().positive(),
  entitlement: z.number().int().positive(),
  selectedWeaponKinds: z.array(z.string()).readonly(),
});

const WizardSpellPreparationDnd5eSchema = z.object({
  source: z.literal("wizard-spellbook"),
  limit: z.number().int().nonnegative(),
  canPrepare: z.boolean(),
  unavailableReason: z.string().optional(),
  entries: z
    .array(
      z.object({
        actionId: z.string(),
        label: z.string(),
        description: z.string(),
        prepared: z.boolean(),
      })
    )
    .readonly(),
});

export const CharacterSheetDnd5eSchema = z.object({
  entityId: z.string(),
  name: z.string(),
  totalLevel: z.number(),
  classes: z
    .array(
      z.object({
        className: z.string(),
        level: z.number(),
        classProfileId: z.string(),
        hitDie: z.number(),
      })
    )
    .readonly(),
  creatureType: z.string().optional(),
  size: z.union([ActorSizeSchema, z.null()]),
  ac: z.number(),
  hp: z.object({ current: z.number(), max: z.number() }),
  initiativeBonus: z.number(),
  proficiencyBonus: z.number(),
  speeds: z.record(z.string(), z.number()),
  abilities: z.record(
    z.string(),
    z.object({ score: z.number(), modifier: z.number(), cap: z.number() })
  ),
  damageResistances: z.array(z.string()).readonly(),
  damageImmunities: z.array(z.string()).readonly(),
  damageVulnerabilities: z.array(z.string()).readonly(),
  conditionImmunities: z.array(z.string()).readonly(),
  proficientSaves: z.array(z.string()).readonly(),
  proficientSkills: z.array(z.string()).readonly(),
  saves: z.record(z.string(), ModListSchema),
  abilityChecks: z.record(z.string(), ModListSchema),
  skills: z.record(z.string(), ModListSchema),
  xp: z.object({
    current: z.number(),
    nextThreshold: z.union([z.number(), z.null()]),
  }),
  featSlots: z.object({
    origin: z.number(),
    general: z.number(),
    fightingStyle: z.number(),
  }),
  subclassSlots: z.number(),
  pendingProfileProficiencyChoices: z
    .array(PendingProfileProficiencyChoiceDnd5eSchema)
    .readonly(),
  pendingExpertiseChoices: z
    .array(PendingExpertiseChoiceDnd5eSchema)
    .readonly(),
  pendingMetamagicChoices: z
    .array(PendingMetamagicChoiceDnd5eSchema)
    .readonly(),
  pendingWeaponMasteryChoices: z
    .array(PendingWeaponMasteryChoiceDnd5eSchema)
    .readonly(),
  spellPreparation: WizardSpellPreparationDnd5eSchema.optional(),
  features: z.array(CharacterFeatureDnd5eSchema).readonly(),
});

type SchemaShape = z.infer<typeof CharacterSheetDnd5eSchema>;

// ---------------------------------------------------------------------------
// Compile-time contract guards (LAF-105 view-shape-drift defence)
//
// _kernelToSchema: load-bearing direction — proves the kernel type is
// assignable to the schema shape, so any required field the kernel removes
// or renames is caught at compile time (a kernel-side field *addition* is not
// caught — structural assignability allows the extra property). Branded/template-literal kernel types
// (DamageTag<"dnd5e">, ConditionTag<"dnd5e">, MovementModeTag<"dnd5e">,
// AbilityScore, Dnd5eSkillName) narrow to string, which is assignable
// into the schema's z.string() fields.
//
// _schemaToKernel: NOT kept. The inverse direction would require the
// schema's plain Record<string,…> to satisfy kernel's
// Record<AbilityScore,…> / Record<Dnd5eSkillName,…> — TypeScript
// cannot narrow string→branded-union without explicit casts, so the
// guard would only compile if we imported every brand into this file,
// which would couple the web schema to kernel internals. The web
// validator intentionally stays permissive (z.record(z.string(),…));
// the _kernelToSchema direction is sufficient to catch all kernel-side
// field additions or removals.
// ---------------------------------------------------------------------------

const _kernelToSchema: SchemaShape = {} as CharacterSheetDnd5e;
